import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download } from "lucide-react";

// Mobile Card Component
const ExpenseCard = ({ expense, index }) => {
  const remaining = expense.budget_amt - expense.expense_amt;
  const percentUsed =
    expense.budget_amt > 0
      ? (expense.expense_amt / expense.budget_amt) * 100
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <p className="text-gray-200 font-semibold text-base mb-2">
            {expense.expense_category_name}
          </p>

          {/* Progress Bar */}
          <div className="w-full bg-gray-700 rounded-full h-2 mb-3 overflow-hidden">
            {percentUsed !== null && (
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  percentUsed > 100
                    ? "bg-red-500"
                    : percentUsed > 80
                    ? "bg-yellow-500"
                    : "bg-green-500"
                }`}
                style={{ width: `${Math.min(percentUsed, 100)}%` }}
              />
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <p className="text-gray-400 text-xs">Spent</p>
              <p className="text-red-400 font-semibold">
                ${Number(expense.expense_amt).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-xs">Budget</p>
              <p className="text-blue-400 font-semibold">
                ${Number(expense.budget_amt).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-xs">Remaining</p>
              <p
                className={`font-semibold ${
                  remaining >= 0 ? "text-green-400" : "text-red-400"
                }`}
              >
                ${Math.abs(remaining).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Percentage Badge */}
          <div className="mt-2">
            <span
              className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                percentUsed === null
                  ? "bg-gray-600/30 text-gray-400"
                  : percentUsed > 100
                  ? "bg-red-500/20 text-red-400"
                  : percentUsed > 80
                  ? "bg-yellow-500/20 text-yellow-400"
                  : "bg-green-500/20 text-green-400"
              }`}
            >
              {percentUsed === null ? "-" : `${percentUsed.toFixed(1)}% used`}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default function ExpenseDetailsModal({ expenses, onClose }) {
  // Sort: budget > 0 first, sorted by percentUsed desc, then budget = 0
  const sortedExpenses = [...expenses].sort((a, b) => {
    const aHasBudget = a.budget_amt > 0;
    const bHasBudget = b.budget_amt > 0;

    if (aHasBudget && !bHasBudget) return -1;
    if (!aHasBudget && bHasBudget) return 1;

    if (!aHasBudget && !bHasBudget) return 0;

    const percentA = (a.expense_amt / a.budget_amt) * 100;
    const percentB = (b.expense_amt / b.budget_amt) * 100;
    return percentB - percentA;
  });

  // 🔽 CSV Download Logic
  const handleDownloadCSV = () => {
    const headers = ["Category", "Expense", "Budget", "Remaining", "% Used"];
    const rows = sortedExpenses.map((exp) => {
      const remaining = exp.budget_amt - exp.expense_amt;
      const percentUsed =
        exp.budget_amt > 0
          ? ((exp.expense_amt / exp.budget_amt) * 100).toFixed(1)
          : "-";
      return [
        exp.expense_category_name,
        `$${Number(exp.expense_amt).toFixed(2)}`,
        `$${Number(exp.budget_amt).toFixed(2)}`,
        `$${Math.abs(remaining).toFixed(2)}`,
        percentUsed === "-" ? "-" : `${percentUsed}%`,
      ];
    });

    const csvContent =
      [headers, ...rows].map((r) => r.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `monthly_expenses_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-start overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl mx-3 sm:mx-4 my-6 sm:my-10"
      >
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 rounded-t-2xl border-b border-gray-700 px-4 sm:px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-blue-400">
            Monthly Expenses
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Download CSV"
            >
              <Download size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {sortedExpenses.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-lg">No expenses to display</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                <AnimatePresence>
                  {sortedExpenses.map((expense, index) => (
                    <ExpenseCard
                      key={expense.id_expense_category || index}
                      expense={expense}
                      index={index}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full border-collapse text-sm md:text-base">
                  <thead className="sticky top-0 bg-gray-900 z-10">
                    <tr className="text-gray-400 border-b border-gray-700">
                      <th className="py-3 px-4 text-left font-semibold">Category</th>
                      <th className="py-3 px-4 text-right font-semibold">Expense</th>
                      <th className="py-3 px-4 text-right font-semibold">Budget</th>
                      <th className="py-3 px-4 text-right font-semibold">Remaining</th>
                      <th className="py-3 px-4 text-right font-semibold">% Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedExpenses.map((exp, i) => {
                      const remaining = exp.budget_amt - exp.expense_amt;
                      const percentUsed =
                        exp.budget_amt > 0
                          ? (exp.expense_amt / exp.budget_amt) * 100
                          : null;

                      return (
                        <motion.tr
                          key={exp.id_expense_category || i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.03 }}
                          className="border-b border-gray-800 hover:bg-gray-800 transition-colors"
                        >
                          <td className="py-3 px-4 text-gray-200 font-medium">
                            {exp.expense_category_name}
                          </td>
                          <td className="py-3 px-4 text-right text-red-400 font-semibold">
                            ${Number(exp.expense_amt).toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right text-blue-400 font-semibold">
                            ${Number(exp.budget_amt).toFixed(2)}
                          </td>
                          <td
                            className={`py-3 px-4 text-right font-semibold ${
                              remaining >= 0 ? "text-green-400" : "text-red-400"
                            }`}
                          >
                            ${Math.abs(remaining).toFixed(2)}
                            {remaining < 0 && " over"}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {percentUsed === null ? (
                              <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-gray-600/30 text-gray-400">
                                -
                              </span>
                            ) : (
                              <span
                                className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                                  percentUsed > 100
                                    ? "bg-red-500/20 text-red-400"
                                    : percentUsed > 80
                                    ? "bg-yellow-500/20 text-yellow-400"
                                    : "bg-green-500/20 text-green-400"
                                }`}
                              >
                                {percentUsed.toFixed(1)}%
                              </span>
                            )}
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
